import LinearProgress from '@mui/material/LinearProgress';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSetOrg from '../components/hooks/useSetOrg';
import useUserInfo from '../components/hooks/useUserInfo';
import { useParams } from 'react-router';
import { useSetTitle } from '../components/hooks/useSetTitle';
import { Box } from '@mui/material';

import TopBar from '../components/common/TopBar';
import DocumentDetailsRight from '../components/documents/details/DocumentDetailsRight';
import DocumentDetailsLeft from '../components/documents/details/DocumentDetailsLeft';
import DocumentViewDocumentModal from '../components/documents/DocumentViewDocumentModal';
import usePaginatedDocumentsManifest from '../components/hooks/usePaginatedDocumentsManifest';
import ErrorMsg from '../components/common/ErrorMsg';
import { DocumentContextProvider } from '../context/DocumentContext';
export interface DocumentDetailsParamTypes {
  [key: string]: string | undefined;
  dpid: string;
  documentId: string;
}

const DocumentDetailsPage = () => {
  const { dpid, documentId } = useParams<DocumentDetailsParamTypes>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | null>(null);

  const { loading } = useSetOrg(dpid);
  const userInfo = useUserInfo();

  if (!documentId) {
    return <>Document ID Required</>;
  }

  const { data: allDocumentData, isPending: documentLoading } = usePaginatedDocumentsManifest({
    limit: 1,
    dpId: dpid!,
    defaultPage: 0,
    includeId: documentId,
  });

  const documentData = allDocumentData?.results[0];

  useSetTitle(`Document Details - ${documentData?.label}`);

  const clampLeftPanelWidth = useCallback((width: number) => {
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? window.innerWidth;
    const maxWidth = Math.max(320, Math.min(760, containerWidth - 560));
    return Math.min(Math.max(width, 260), maxWidth);
  }, []);

  const handleResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        setLeftPanelWidth(clampLeftPanelWidth(moveEvent.clientX - containerLeft));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [clampLeftPanelWidth]
  );

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();
      const delta = event.shiftKey ? 40 : 16;
      const currentWidth =
        leftPanelWidth ?? (containerRef.current?.getBoundingClientRect().width ?? window.innerWidth) * 0.25;
      setLeftPanelWidth((width) =>
        clampLeftPanelWidth((width ?? currentWidth) + (event.key === 'ArrowRight' ? delta : -delta))
      );
    },
    [clampLeftPanelWidth, leftPanelWidth]
  );

  useEffect(() => {
    const handleWindowResize = () => {
      setLeftPanelWidth((width) => (width === null ? null : clampLeftPanelWidth(width)));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [clampLeftPanelWidth]);

  if (userInfo.isLoading || loading || documentLoading) {
    return <LinearProgress />;
  }

  if (!documentData) {
    return (
      <>
        <TopBar />
        <ErrorMsg error="Document not found" />
      </>
    );
  }

  return (
    <DocumentContextProvider>
      <TopBar />
      <Box ref={containerRef} sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        <Box sx={{ width: leftPanelWidth ?? '25%', flexShrink: 0, minWidth: 0 }}>
          <DocumentDetailsLeft documentData={documentData} />
        </Box>
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize document details sidebar"
          tabIndex={0}
          onPointerDown={handleResizeStart}
          onKeyDown={handleResizeKeyDown}
          sx={{
            flex: '0 0 10px',
            cursor: 'col-resize',
            position: 'relative',
            zIndex: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '4px',
              width: '2px',
              backgroundColor: 'custom.lightGray',
              transition: 'background-color 0.15s, width 0.15s',
            },
            '&:hover::before, &:focus-visible::before': {
              left: '3px',
              width: '4px',
              backgroundColor: '#66BBA9',
            },
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DocumentDetailsRight documentData={documentData} />
        </Box>
      </Box>
      <DocumentViewDocumentModal />
    </DocumentContextProvider>
  );
};

export default DocumentDetailsPage;
